import callClientPortal from '../../lib/callClientPortal';
import timestamp from '../../../esm/lib/time-stamp.js';
import jsYaml from 'js-yaml';

import {
  createNewDraft,
  getLatestDraft,
  getLatestSaved,
  getAllVersions,
  updateDraft,
  commitDraft,
  publishCourse,
  uploadTsv,
  getTranslations,
  createAiTranslations,
  getNextBaseCourseId,
  getNextBespokeCourseId,
  getLatestResourceDraftOrSaved
} from '../../lib/callModContent';

export {
  createNewDraft,
  getLatestDraft,
  getLatestSaved,
  getAllVersions,
  updateDraft,
  commitDraft,
  publishCourse,
  uploadTsv,
  getTranslations,
  createAiTranslations,
  getNextBaseCourseId,
  getNextBespokeCourseId,
  getLatestResourceDraftOrSaved
};

const LATEST = encodeURIComponent(`'metadata'->'createdDate':desc`);

export const listSaved = async () => {
  const response = await callClientPortal<CourseResponse>(
    `/saved?compact=true&limit=99999&distinct=true&sort=${LATEST}`
  );

  return response.data.data.results || [];
};

interface TranslatedPhrase {
  courseId: number;
  firmCode?: string;
  lang: string;
  key: string;
  en: string;
  translation: string;
  type: string;
}

export const upsertTranslations = async (data: TranslatedPhrase[]) => {
  const body = new FormData();
  body.append('translationData', JSON.stringify(data));

  const response = await callClientPortal<CourseResponse>(`/translations`, {
    method: 'POST',
    body
  });

  return response.data.data;
};

interface AudioParam {
  courseId: number;
  firmCode: string;
  lang: string;
  audioJSON: string;
  voiceData: string;
  uploadAudio: boolean;
}

export const createAIAudio = async (data: AudioParam) => {
  const body = new FormData();

  body.append('audioData', JSON.stringify(data));

  const response = await callClientPortal(`/audio/ai`, {
    method: 'POST',
    body
  });

  return response.data.data.results;
};

interface VoicePreviewParam {
  lang: string;
  text: string;
  voiceData: string;
}

export const createVoicePreview = async (data: VoicePreviewParam) => {
  const body = new FormData();

  body.append('audioData', JSON.stringify(data));

  const response = await callClientPortal(`/audio/ai/preview`, {
    method: 'POST',
    body
  });

  return response.data.data.results;
};

export const getVoiceList = async () => {
  const queryParams = 'voiceList=true';

  const response = await callClientPortal(`/audio/ai/preview?${queryParams}`, {
    method: 'GET'
  });

  return response;
};

export const getVoiceSettings = async (firmCode: string, courseId: number) => {
  const queryParams =
    `courseId=${courseId}` + (firmCode ? `&firmCode=${firmCode}` : '');

  const response = await callClientPortal(
    `/audio/ai/voicesettings?${queryParams}`,
    {
      method: 'GET'
    }
  );

  return response;
};

const defaultOrigin =
  window.VITE_LIBRARY_API_BASE || import.meta.env.VITE_LIBRARY_API_BASE;

export const publishBespoke = async data => {
  const body = JSON.stringify(data);

  const response = await callClientPortal(`/library/authoring/bespoke`, {
    method: 'POST',
    body
  });

  return response;
};

export const getSuitesAndTopics = async () => {
  const response = await callClientPortal(
    `/public/suite-topic`,
    {
      method: 'GET'
    },
    { base: defaultOrigin + '/api/v1' }
  ).then(res => {
    const sortedRes = (res.data.data.results || []).filter(
      (e: { internalId: number }) => e.internalId !== 6
    );

    const compareFunc = (a, b) =>
      a.title > b.title ? 1 : b.title > a.title ? -1 : 0;

    sortedRes.sort(compareFunc);
    return sortedRes;
  });

  return response;
};

export const getActivityTypes = async () => {
  const response = await callClientPortal(`/metadata/activity-types`, {
    method: 'GET'
  });

  return response;
};

export const getIndustries = async () => {
  const response = await callClientPortal(`/metadata/industries`, {
    method: 'GET'
  });

  return response;
};

export const getJurisdictions = async () => {
  const response = await callClientPortal(`/metadata/jurisdictions`, {
    method: 'GET'
  });

  return response;
};

export const getAccreditations = async () => {
  const response = await callClientPortal(`/metadata/accreditations`, {
    method: 'GET'
  });

  return response;
};

export const publishCourseToLibrary = async (payloadToNormalize: any) => {
  // The content API expects a wrapper { resourceData: ... }
  const response = await callClientPortal(`/library/courses`, {
    method: 'POST',
    body: JSON.stringify({ resourceData: payloadToNormalize })
  });
  return response;
};

export const updateCourseInLibrary = async (
  courseId: number,
  payloadToNormalize: any
) => {
  const response = await callClientPortal(`/library/courses/${courseId}`, {
    method: 'PUT',
    body: JSON.stringify({ resourceData: payloadToNormalize })
  });
  return response;
};

export const deleteCourseFromLibrary = async (courseId: number) => {
  const response = await callClientPortal(`/library/courses/${courseId}`, {
    method: 'DELETE'
  });
  return response;
};

// ============================================================================
// Resource API Functions
// ============================================================================

// Get resource from library (published state)
export const getResourceFromLibrary = async (resourceId: number) => {
  const response = await callClientPortal(`/library/courses/${resourceId}`, {
    method: 'GET'
  });
  return response.data?.data || response.data;
};

// Get draft metadata from saved_items_table JSONB
// Returns the entire JSONB object (not a draftLibraryMetadata namespace)
// Uses modified backend endpoint that detects resource type
export const getResourceDraftMetadata = async (resourceId: number) => {
  try {
    const response = await callClientPortal(
      `/courses/${resourceId}/draft-metadata`,
      { method: 'GET' }
    );

    if (response.response.status === 404) {
      return null; // No saved item exists yet
    }

    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null; // No saved item exists yet
    }
    throw new Error(
      `Failed to fetch resource draft metadata: ${error.message}`
    );
  }
};

// Update resource metadata section
// Merges section fields into existing JSONB and updates whole row
// Uses modified backend endpoint that handles resource type
export const updateResourceDraftMetadataSection = async (
  resourceId: number,
  sectionName: string,
  sectionData: any, // Object with field paths as keys (e.g., { "title": "...", "additionalInfo.Industry": "..." })
  sectionTitle?: string
) => {
  const url = `/courses/${resourceId}/draft-metadata/${sectionName}`;

  try {
    const requestBody: any = { ...sectionData };
    // Include sectionTitle in request body if provided
    if (sectionTitle) {
      requestBody._sectionTitle = sectionTitle;
    }

    const response = await callClientPortal(url, {
      method: 'PUT',
      body: JSON.stringify(requestBody)
    });

    // Handle different response types (204 No Content or JSON)
    if (response.response.status === 204) {
      return { success: true, message: 'Section updated successfully' };
    }

    return response.data || { success: true };
  } catch (error: any) {
    console.error('Error updating resource draft metadata section:', error);
    throw new Error(`Failed to save ${sectionName}: ${error.message}`);
  }
};

// Publish resource to library (new resource)
export const publishResourceToLibrary = async (payloadToNormalize: any) => {
  const response = await callClientPortal(`/library/courses`, {
    method: 'POST',
    body: JSON.stringify({
      resourceData: { ...payloadToNormalize, type: 'resource' }
    })
  });
  return response.data?.data || response.data;
};

// Update resource in library (existing resource)
export const updateResourceInLibrary = async (
  resourceId: number,
  payloadToNormalize: any
) => {
  const response = await callClientPortal(`/library/courses/${resourceId}`, {
    method: 'PUT',
    body: JSON.stringify({
      resourceData: { ...payloadToNormalize, type: 'resource' }
    })
  });
  return response.data?.data || response.data;
};

// Delete resource from library
export const deleteResourceFromLibrary = async (resourceId: number) => {
  const response = await callClientPortal(`/library/courses/${resourceId}`, {
    method: 'DELETE'
  });
  return response;
};

export const getTargetLearners = async () => {
  const response = await callClientPortal(`/metadata/target-learners`, {
    method: 'GET'
  });

  return response;
};

export const getCourseFromLibrary = async (courseId: number) => {
  const response = await callClientPortal(`/library/courses/${courseId}`, {
    method: 'GET'
  });
  return response;
};

export type LibraryListType = 'course' | 'resource';

export const getAllCoursesFromLibrary = async (options?: {
  type?: LibraryListType;
}) => {
  const type = options?.type;
  const url =
    type === 'resource' ? `/library/courses?type=resource` : `/library/courses`;
  const response = await callClientPortal(url, {
    method: 'GET'
  });
  return response;
};

export const getDraftMetadata = async (courseId: number) => {
  try {
    const response = await callClientPortal(
      `/courses/${courseId}/draft-metadata`,
      { method: 'GET' }
    );

    if (response.response.status === 404) {
      return null; // No draft exists yet
    }

    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null; // No draft exists yet
    }
    throw new Error(`Failed to fetch draft metadata: ${error.message}`);
  }
};

export const updateDraftMetadataSection = async (
  courseId: number,
  sectionName: string,
  sectionData: any,
  sectionTitle?: string,
  isPublishSync?: boolean
) => {
  const url = `/courses/${courseId}/draft-metadata/${sectionName}`;
  console.log('Updating draft metadata section:', {
    url,
    courseId,
    sectionName,
    sectionData,
    sectionTitle
  });

  try {
    const requestBody: any = { ...sectionData };
    // Include sectionTitle in request body if provided
    if (sectionTitle) {
      requestBody._sectionTitle = sectionTitle;
    }
    // Include isPublishSync flag if provided
    if (isPublishSync) {
      requestBody._isPublishSync = true;
    }

    const response = await callClientPortal(url, {
      method: 'PUT',
      body: JSON.stringify(requestBody)
    });

    console.log(
      'Response status:',
      response.response.status,
      'Response headers:',
      response.response.headers
    );

    // Handle different response types
    if (response.response.status === 204) {
      // No Content - successful update with no response body
      return { success: true, message: 'Section updated successfully' };
    }

    // Try to parse JSON response if there is content
    const contentType = response.response.headers['content-type'];
    if (contentType && contentType.includes('application/json')) {
      return response.data;
    }

    // Return success for other successful responses without JSON
    return { success: true, message: 'Section updated successfully' };
  } catch (error) {
    console.error('Error updating draft metadata section:', error);
    throw new Error(`Failed to save ${sectionName}: ${error.message}`);
  }
};

export const getClientBespokeCourses = async (
  firmCode: string,
  courseIds: number[]
) => {
  const queryParams = courseIds.map(id => `vinciResourceId=${id}`).join('&');

  const response = await callClientPortal(
    `/public/courses/bespoke?${queryParams}`,
    {
      method: 'GET',
      headers: {
        'x-tenant-id': firmCode,
        'Content-Type': 'application/json'
      }
    },
    { base: defaultOrigin + '/api/v1' }
  );

  return response;
};

export const exportTsv = async ({
  courseId,
  firmCode,
  to,
  download = true,
  suffix = '',
  includeAll
}) => {
  let route = firmCode
    ? `/translations/tsv?courseId=${courseId}&firmCode=${firmCode}&to=${to}`
    : `/translations/tsv?courseId=${courseId}&to=${to}`;

  if (includeAll) route += '&includeAll=true';

  const response = await callClientPortal(route);
  let { filename, tsv, typeCounts } = response.data;

  if (!filename || !tsv || !typeCounts) {
    throw new Error('No data returned from exportTsv');
  }

  if (suffix) {
    filename = filename.replace('.tsv', `_${suffix}.tsv`);
  }

  if (download) {
    // filename from Content-Disposition header
    // const filename = response.headers['content-disposition'].split('=')[1];
    const tsvBlob = window.URL.createObjectURL(new Blob([tsv]));
    const link = document.createElement('a');
    link.href = tsvBlob;
    link.setAttribute('download', filename);
    link.classList.add('sr-only');
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  return { response, filename, tsv, typeCounts };
};

export const downloadTsv = async () => {
  const response = await callClientPortal(`/translations/tsv`, {
    responseType: 'blob'
  });

  const temp = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = temp;
  link.setAttribute('download', 'file.tsv'); //or any other extension
  document.body.appendChild(link);
  link.click();
  link.remove();

  return response;
};

// TODO add compact=true to drafts API
// export const listDrafts = async () => {
//   const response = await callClientPortal<CourseResponse>(
//     `/drafts?query=[[{"field":"type","op":"=","val":"course"}]]&limit=9999`
//   );

//   return response.data.data.results || [];
// };

// export const listDrafts = async () => {
//   const response = await callClientPortal<CourseResponse>('/drafts?limit=9999');

//   const { results } = response.data.data;

//   if (!results) {
//     throw new Error('No results');
//   }

//   results.forEach(r => {
//   });

//   return response.data.data.results;
// };

export const getPublished = async ({ courseId, firmCode }) => {
  let route = `/publish?limit=9999&order=desc&courseId=${courseId}`;
  if (firmCode) route += `&firmCode=${firmCode}`;

  const response = await callClientPortal<PublishBody>(route);
  const results = response.data.data.results;
  if (!results) return [];
  results.sort((a, b) => {
    if (!a?.metadata || !b?.metadata) return 0;
    return b.metadata.createdDate - a.metadata.createdDate;
  });

  return results;
};

export const publishTranslations = async ({
  courseId,
  firmCode,
  to,
  provider = 'azure',
  translateExistingCaptions = false,
  publishTo = 'live'
}: TranslationParams) => {
  const devOrQA =
    window.location.hostname.includes('-dev') ||
    window.location.hostname.includes('-qa');
  const prodParam = !devOrQA ? '&prod=true' : '';

  let route = firmCode
    ? `/translations/publish?courseId=${courseId}&to=${to}&firmCode=${firmCode}&provider=${provider}&publishTo=${publishTo}${prodParam}`
    : `/translations/publish?courseId=${courseId}&to=${to}&provider=${provider}&publishTo=${publishTo}${prodParam}`;

  if (translateExistingCaptions) route += '&translateExistingCaptions=true';

  return callClientPortal(route, { method: 'POST' });
};

export interface BulkTranslationFailedItem {
  courseId: number;
  firmCode?: string;
  lang: string;
  error: string | null;
  reason: string | null;
}

export interface BulkTranslationJobSummary {
  batchId: string;
  submittedBy: string;
  submittedAt: string;
  provider: string;
  refreshAi: boolean;
  total: number;
  completed: number;
  failed: number;
  skipped: number;
  inProgress: number;
  languages: string[];
  baseCourses: string[];
  customCourses: string[];
  firms: string[];
  customisations: { courseId: number; firmCode: string }[];
  baseCompleted: number;
  baseFailed: number;
  customCompleted: number;
  customFailed: number;
  failedBaseItems: BulkTranslationFailedItem[];
  failedCustomItems: BulkTranslationFailedItem[];
}

export const getBulkTranslationJobs = async (): Promise<
  BulkTranslationJobSummary[]
> => {
  const response = await callClientPortal('/translations/bulk-jobs');
  const data = (response.data as any).data;
  return Array.isArray(data) ? data : data?.results || [];
};

export interface BulkTranslationJobParams {
  courses: { courseId: number; firmCode: string }[];
  languages: string[];
  provider?: string;
  refreshAi?: boolean;
  publishToProduction?: boolean;
  skipBaseTranslation?: boolean;
  notifyEmail?: string;
}

export const submitBulkTranslationJob = async (
  params: BulkTranslationJobParams
): Promise<void> => {
  await callClientPortal('/translations/bulk-job', {
    method: 'POST',
    body: JSON.stringify(params)
  });
};

export const retryBulkTranslationJob = async (
  job: BulkTranslationJobSummary
): Promise<void> => {
  let courses: { courseId: number; firmCode: string }[];
  let languages: string[];

  if (job.baseFailed > 0) {
    // Base failed — retry all failed/skipped items (base + associated customisations)
    const uniqueCourses = new Map<
      string,
      { courseId: number; firmCode: string }
    >();
    for (const item of [...job.failedBaseItems, ...job.failedCustomItems]) {
      const key = `${item.courseId}_${item.firmCode ?? ''}`;
      uniqueCourses.set(key, {
        courseId: item.courseId,
        firmCode: item.firmCode ?? ''
      });
    }
    courses = [...uniqueCourses.values()];
    languages = [
      ...new Set([
        ...job.failedBaseItems.map(i => i.lang),
        ...job.failedCustomItems.map(i => i.lang)
      ])
    ];
  } else {
    // Only customisations failed — retry just those
    const uniqueCourses = new Map<
      string,
      { courseId: number; firmCode: string }
    >();
    for (const item of job.failedCustomItems) {
      const key = `${item.courseId}_${item.firmCode ?? ''}`;
      uniqueCourses.set(key, {
        courseId: item.courseId,
        firmCode: item.firmCode ?? ''
      });
    }
    courses = [...uniqueCourses.values()];
    languages = [...new Set(job.failedCustomItems.map(i => i.lang))];
  }

  await submitBulkTranslationJob({
    courses,
    languages,
    provider: job.provider
  });
};

export const checkStyleGuide = async (courseIds: number[]) => {
  // This function is currently a placeholder and needs implementation.

  const courseIdsParam = courseIds.join(',');
  console.log('Calling checkStyleGuide with courseIds: ', courseIdsParam);
  return await callClientPortal(
    `/tests/styleguide?courseIds=${courseIdsParam}`
  );
};

export const checkVideoCors = async (courseId: number, firmCode: string) => {
  const route =
    firmCode && firmCode !== '___'
      ? `/tests/corsheaders?courseId=${courseId}&firm=${firmCode}`
      : `/tests/corsheaders?courseId=${courseId}`;
  return await callClientPortal(route);
};

// Helper function to extract YAML context using JSON structure
const extractYamlContext = (yaml: string, search: string) => {
  const lines = yaml.split('\n');
  const searchLower = search.toLowerCase();

  // Map to store contexts by property path (to group matches)
  const contextsByProperty = new Map<
    string,
    {
      property: string;
      path: string;
      value: string;
      lineNumbers: number[];
    }
  >();

  try {
    // Parse YAML to JSON to understand structure
    const json = jsYaml.load(yaml) as any;

    if (!json || typeof json !== 'object') {
      // Fallback to line-based approach if parsing fails
      return extractYamlContextFallback(yaml, search);
    }

    // Helper to find property path for a value containing search term in JSON
    const findPropertyPath = (
      obj: any,
      searchTerm: string,
      path: string[] = [],
      visited = new WeakSet()
    ): string[] | null => {
      // Prevent circular references
      if (obj && typeof obj === 'object') {
        if (visited.has(obj)) return null;
        visited.add(obj);
      }

      if (
        typeof obj === 'string' &&
        obj.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        return path;
      }

      if (Array.isArray(obj)) {
        for (let i = 0; i < obj.length; i++) {
          const item = obj[i];
          if (typeof item === 'object' && item !== null) {
            // For array items that are objects, recurse into them
            const itemPath = findPropertyPath(
              item,
              searchTerm,
              [...path, String(i)],
              visited
            );
            if (itemPath) return itemPath;
          } else if (
            typeof item === 'string' &&
            item.toLowerCase().includes(searchTerm.toLowerCase())
          ) {
            // Found in array item - return path with index
            return [...path, String(i)];
          }
        }
      } else if (obj && typeof obj === 'object') {
        for (const [key, value] of Object.entries(obj)) {
          const newPath = findPropertyPath(
            value,
            searchTerm,
            [...path, key],
            visited
          );
          if (newPath) return newPath;
        }
      }

      return null;
    };

    // Build a map of search term occurrences to their property paths
    const searchOccurrences: Array<{ path: string[]; value: any }> = [];
    const collectSearchOccurrences = (
      obj: any,
      path: string[] = [],
      visited = new WeakSet()
    ) => {
      if (obj && typeof obj === 'object') {
        if (visited.has(obj)) return;
        visited.add(obj);
      }

      if (typeof obj === 'string' && obj.toLowerCase().includes(searchLower)) {
        searchOccurrences.push({ path: [...path], value: obj });
      }

      if (Array.isArray(obj)) {
        obj.forEach((item, i) => {
          collectSearchOccurrences(item, [...path, String(i)], visited);
        });
      } else if (obj && typeof obj === 'object') {
        Object.entries(obj).forEach(([key, value]) => {
          collectSearchOccurrences(value, [...path, key], visited);
        });
      }
    };

    collectSearchOccurrences(json);

    // Find all matching lines and identify their property paths
    lines.forEach((line, index) => {
      const lineWithoutComment = line.split('#')[0].trim();
      if (lineWithoutComment.toLowerCase().includes(searchLower)) {
        // Try to find which property this line belongs to by matching against collected occurrences
        let matchedPath: string[] | null = null;

        // Find the best matching property path for this line
        // We'll match by checking if the line content appears in any of the collected occurrences
        for (const occurrence of searchOccurrences) {
          const occurrenceStr =
            typeof occurrence.value === 'string'
              ? occurrence.value
              : JSON.stringify(occurrence.value);

          // Check if this line's content is part of the occurrence value
          if (
            occurrenceStr
              .toLowerCase()
              .includes(lineWithoutComment.toLowerCase()) ||
            lineWithoutComment
              .toLowerCase()
              .includes(occurrenceStr.toLowerCase().substring(0, 50))
          ) {
            matchedPath = occurrence.path;
            break;
          }
        }

        // If no direct match, try to find by searching JSON
        if (!matchedPath) {
          matchedPath = findPropertyPath(
            json,
            lineWithoutComment.substring(0, 100)
          );
        }

        if (matchedPath && matchedPath.length > 0) {
          // Use the last key in the path as the property name
          // For nested paths like ['pages', '0', 'screens', '0', 'text'], use 'text'
          // For array items like ['options', '1'], use the array key '1' or parent 'options'
          const propertyName = matchedPath[matchedPath.length - 1];
          const propertyPath = matchedPath.join('.');

          // Get or create context for this property
          if (!contextsByProperty.has(propertyName)) {
            // Extract the YAML value for this property
            const value = extractYamlValueForPath(lines, matchedPath, json);
            contextsByProperty.set(propertyName, {
              property: propertyName,
              path: propertyPath,
              value: value,
              lineNumbers: []
            });
          }

          // Add this line number to the context
          const context = contextsByProperty.get(propertyName)!;
          if (!context.lineNumbers.includes(index + 1)) {
            context.lineNumbers.push(index + 1);
          }
        } else {
          // Fallback: use line-based approach for this match
          const fallbackResult = findPropertyKeyFallback(lines, index);
          if (fallbackResult) {
            const { key, keyLineIndex } = fallbackResult;
            if (!contextsByProperty.has(key)) {
              const value = collectPropertyValueFallback(lines, keyLineIndex);
              contextsByProperty.set(key, {
                property: key,
                path: '',
                value: value,
                lineNumbers: []
              });
            }
            contextsByProperty.get(key)!.lineNumbers.push(index + 1);
          }
        }
      }
    });
  } catch (error) {
    // If YAML parsing fails, fall back to line-based approach
    console.warn('YAML parsing failed, using fallback:', error);
    return extractYamlContextFallback(yaml, search);
  }

  // Convert to array format, using the first line number for each property
  return Array.from(contextsByProperty.values()).map(context => ({
    property: context.property,
    path: context.path,
    value: context.value,
    lineNumber: context.lineNumbers[0]
  }));
};

// Fallback function using line-based approach
const extractYamlContextFallback = (yaml: string, search: string) => {
  const lines = yaml.split('\n');
  const searchLower = search.toLowerCase();
  const contextsByProperty = new Map<
    string,
    {
      property: string;
      path: string;
      value: string;
      lineNumbers: number[];
    }
  >();

  lines.forEach((line, index) => {
    const lineWithoutComment = line.split('#')[0];
    if (lineWithoutComment.toLowerCase().includes(searchLower)) {
      const propertyInfo = findPropertyKeyFallback(lines, index);
      if (propertyInfo) {
        const { key, keyLineIndex } = propertyInfo;
        if (!contextsByProperty.has(key)) {
          const value = collectPropertyValueFallback(lines, keyLineIndex);
          contextsByProperty.set(key, {
            property: key,
            path: '',
            value: value,
            lineNumbers: []
          });
        }
        contextsByProperty.get(key)!.lineNumbers.push(index + 1);
      }
    }
  });

  return Array.from(contextsByProperty.values()).map(context => ({
    property: context.property,
    path: context.path,
    value: context.value,
    lineNumber: context.lineNumbers[0]
  }));
};

// Helper to extract YAML value for a JSON path
const extractYamlValueForPath = (
  lines: string[],
  path: string[],
  json: any
): string => {
  // Navigate JSON to get the value
  let value: any = json;
  for (const key of path) {
    if (Array.isArray(value)) {
      const idx = parseInt(key);
      if (!isNaN(idx) && idx >= 0 && idx < value.length) {
        value = value[idx];
      } else {
        break;
      }
    } else if (value && typeof value === 'object') {
      value = value[key];
    } else {
      break;
    }
  }

  // Convert back to YAML string for display
  if (typeof value === 'string') {
    return value;
  }

  // For complex values, convert back to YAML string
  try {
    return jsYaml.dump(value, { lineWidth: -1, indent: 2 }).trim();
  } catch {
    return String(value);
  }
};

// Fallback helper to find property key
const findPropertyKeyFallback = (
  lines: string[],
  lineIndex: number
): { key: string; keyLineIndex: number } | null => {
  const line = lines[lineIndex];
  const currentIndent = line.match(/^\s*/)?.[0].length || 0;

  // Check if this line itself has a property key
  let sameLineMatch = line.match(/^\s*(?:-\s*)?(['"])([a-zA-Z0-9_-]+)\1\s*:/);
  if (!sameLineMatch) {
    sameLineMatch = line.match(/^\s*(?:-\s*)?([a-zA-Z0-9_-]+)\s*:/);
  }
  if (sameLineMatch) {
    return {
      key: sameLineMatch[2] || sameLineMatch[1],
      keyLineIndex: lineIndex
    };
  }

  // Look backwards
  for (let i = lineIndex - 1; i >= 0; i--) {
    const prevLine = lines[i];
    const prevIndent = prevLine.match(/^\s*/)?.[0].length || 0;
    const prevTrimmed = prevLine.trim();

    if (prevTrimmed.startsWith('#') && prevIndent >= currentIndent) {
      continue;
    }

    if (prevIndent < currentIndent) {
      let keyMatch = prevLine.match(
        /^\s*(?:-\s*)?(['"])([a-zA-Z0-9_-]+)\1\s*:/
      );
      if (!keyMatch) {
        keyMatch = prevLine.match(/^\s*(?:-\s*)?([a-zA-Z0-9_-]+)\s*:/);
      }
      if (keyMatch) {
        return { key: keyMatch[2] || keyMatch[1], keyLineIndex: i };
      }
    }

    if (
      prevIndent <= currentIndent &&
      prevTrimmed &&
      !prevTrimmed.startsWith('#')
    ) {
      break;
    }
  }

  return null;
};

// Fallback helper to collect property value
const collectPropertyValueFallback = (
  lines: string[],
  keyLineIndex: number
): string => {
  const keyLine = lines[keyLineIndex];
  const keyIndent = keyLine.match(/^\s*/)?.[0].length || 0;
  const propertyLines: string[] = [keyLine.trim()];
  const hasMultilineIndicator = /:\s*[|>]/.test(keyLine);

  for (let j = keyLineIndex + 1; j < lines.length; j++) {
    const nextLine = lines[j];
    const nextIndent = nextLine.match(/^\s*/)?.[0].length || 0;
    const nextTrimmed = nextLine.trim();

    if (!nextTrimmed || nextTrimmed === '#') {
      if (hasMultilineIndicator && nextIndent > keyIndent) {
        propertyLines.push(nextLine);
        continue;
      }
      if (nextIndent <= keyIndent) {
        break;
      }
      continue;
    }

    if (nextIndent <= keyIndent) {
      const nextKeyMatch = nextTrimmed.match(
        /^(?:-\s*)?(['"]?)([a-zA-Z0-9_-]+)\1\s*:/
      );
      if (nextKeyMatch) {
        break;
      }
    }

    if (
      nextIndent > keyIndent ||
      (nextIndent === keyIndent &&
        !nextTrimmed.match(/^(?:-\s*)?(['"]?)([a-zA-Z0-9_-]+)\1\s*:/))
    ) {
      propertyLines.push(nextLine);
    } else {
      break;
    }
  }

  return propertyLines.filter(l => l !== null && l !== undefined).join('\n');
};

// Helper function to extract CSS context
const extractCssContext = (css: string, search: string) => {
  const lines = css.split('\n');
  const contexts: Array<{
    property: string;
    value: string;
    lineNumber: number;
  }> = [];

  lines.forEach((line, index) => {
    if (line.toLowerCase().includes(search.toLowerCase())) {
      // For CSS, find the selector/rule that contains this line
      let selector = '';
      let ruleLines: string[] = [];

      // Look backwards to find the selector (CSS rule starts with a selector and ends with {)
      for (let i = index; i >= 0; i--) {
        const currentLine = lines[i].trim();
        // Check if this is a CSS selector line (contains { or is a selector pattern)
        if (currentLine && currentLine.includes('{')) {
          selector = currentLine.replace('{', '').trim();
          // Collect all lines in this rule block
          for (let j = i; j < lines.length; j++) {
            ruleLines.push(lines[j]);
            if (lines[j].trim() === '}' && j > i) {
              break;
            }
          }
          break;
        } else if (
          currentLine &&
          !currentLine.includes(':') &&
          currentLine.length > 0 &&
          i < index
        ) {
          // This might be a multi-line selector, continue looking backwards
          continue;
        }
      }

      // If we didn't find a selector, check if this line itself is a property
      if (!selector) {
        const propertyMatch = line.trim().match(/^([a-zA-Z0-9_-]+)\s*:/);
        if (propertyMatch) {
          selector = propertyMatch[1];
          ruleLines = [line];
        } else {
          selector = 'CSS rule';
          ruleLines = [line];
        }
      }

      contexts.push({
        property: selector || 'unknown',
        value: ruleLines.join('\n'),
        lineNumber: index + 1
      });
    }
  });

  return contexts;
};

// Exported function to search courses with context
export const searchCoursesWithContext = async (
  search: string,
  field: 'yaml' | 'css' = 'yaml'
) => {
  const query = encodeURIComponent(
    JSON.stringify([
      [{ field, op: 'LIKE', val: encodeURIComponent(`%${search}%`) }]
    ])
  );

  const response = await callClientPortal(
    `/saved?limit=99999&query=${query}&distinct=true&sort='metadata'->'updatedDate':desc`
  );

  const results = response.data.data.results;

  // Extract context for each result
  const matchingResults =
    results?.map((result: CourseResponse) => {
      const content = field === 'yaml' ? result?.yaml : result?.css;
      if (!content) return { ...result, context: [] };

      const contexts =
        field === 'yaml'
          ? extractYamlContext(content, search)
          : extractCssContext(content, search);

      return {
        ...result,
        context: contexts
      };
    }) || [];

  return matchingResults;
};

window.searchForYaml = async (search: string, compact: boolean = true) => {
  const query = encodeURIComponent(
    JSON.stringify([
      [{ field: 'yaml', op: 'LIKE', val: encodeURIComponent(`%${search}%`) }]
    ])
  );

  const response = await callClientPortal(
    `/saved?limit=99999&query=${query}&distinct=true${
      compact ? '&compact=true' : ''
    }&sort='metadata'->'updatedDate':desc`
  );

  const results = response.data.data.results;

  if (compact) return results;

  // find the matching string in the yaml field and extract the matching text property/context for each result
  const matchingResults =
    results?.map((result: CourseResponse) => {
      const yaml = result?.yaml;
      if (!yaml) return { ...result, context: [] };

      const contexts = extractYamlContext(yaml, search);

      return {
        ...result,
        context: contexts
      };
    }) || [];

  /*const justTheContexts = matchingResults.map(result => ({
    courseId: result.courseId,
    firmCode: result.firmCode,
    context: result.context
  }));*/

  return matchingResults;
};

// @ts-ignore
window.checkStyleGuide = checkStyleGuide;
// @ts-ignore
window.checkVideoCors = checkVideoCors;
// @ts-ignore
window.getLatestDraft = getLatestDraft;
// @ts-ignore
window.updateDraft = updateDraft;
// @ts-ignore
window.createNewDraft = createNewDraft;
// @ts-ignore
window.commitDraft = commitDraft;
// @ts-ignore
window.listSaved = listSaved;
// @ts-ignore
window.callClientPortal = callClientPortal;
// @ts-ignore
window.getTranslations = getTranslations;
// @ts-ignore
window.exportTsv = exportTsv;
// @ts-ignore
window.uploadTsv = uploadTsv;
// @ts-ignore
window.downloadTsv = downloadTsv;
// @ts-ignore
window.createAiTranslations = createAiTranslations;
// @ts-ignore
window.publishTranslations = publishTranslations;
// @ts-ignore
window.getPublished = getPublished;
// @ts-ignore
window.createAIAudio = createAIAudio;
// @ts-ignore
window.publishBespoke = publishBespoke;
// @ts-ignore
window.getSuitesAndTopics = getSuitesAndTopics;
// @ts-ignore
window.getClientBespokeCourses = getClientBespokeCourses;
// @ts-ignore
window.getActivityTypes = getActivityTypes;
// @ts-ignore
window.getIndustries = getIndustries;
// @ts-ignore
window.getJurisdictions = getJurisdictions;
// @ts-ignore
window.getAccreditations = getAccreditations;
// @ts-ignore
window.getTargetLearners = getTargetLearners;
// @ts-ignore
window.getCourseFromLibrary = getCourseFromLibrary;
// @ts-ignore
window.getAllCoursesFromLibrary = getAllCoursesFromLibrary;
// @ts-ignore
window.getDraftMetadata = getDraftMetadata;
// @ts-ignore
window.updateDraftMetadataSection = updateDraftMetadataSection;
// @ts-ignore
window.getResourceFromLibrary = getResourceFromLibrary;
// @ts-ignore
window.getResourceDraftMetadata = getResourceDraftMetadata;
// @ts-ignore
window.updateResourceDraftMetadataSection = updateResourceDraftMetadataSection;
// @ts-ignore
window.publishResourceToLibrary = publishResourceToLibrary;
// @ts-ignore
window.updateResourceInLibrary = updateResourceInLibrary;

// Thumbnail-related functions
const getThumbnailBucketUrl = () => {
  // Check if we're in a test environment based on hostname
  const isTestEnv =
    window.location.hostname.includes('-dev') ||
    window.location.hostname.includes('-qa');

  const bucketName = isTestEnv
    ? 'vinciworks-client-portal-assets-test'
    : 'vinciworks-client-portal-assets';

  return `https://${bucketName}.s3.eu-west-2.amazonaws.com`;
};

export const checkThumbnailExists = async (filename: string) => {
  return new Promise<boolean>(resolve => {
    const img = new Image();
    // Add cache-busting parameter to force fresh check
    const url = `${getThumbnailBucketUrl()}/${filename}?t=${Date.now()}`;

    img.onload = () => {
      console.log('Thumbnail exists:', filename);
      resolve(true);
    };

    img.onerror = () => {
      console.log('Thumbnail does not exist:', filename);
      resolve(false);
    };

    // Set timeout to avoid hanging
    setTimeout(() => {
      console.log('Thumbnail check timeout:', filename);
      resolve(false);
    }, 5000);

    img.src = url;
  });
};

export const checkAllThumbnailsExist = async (courseId: number) => {
  const thumbnailTypes = [
    { name: 'standard', filename: `${courseId}.jpg` },
    { name: 'hero', filename: `${courseId}-hero.jpg` },
    { name: 'tile', filename: `${courseId}-tile.jpg` },
    { name: 'noTitle', filename: `${courseId}-notitle.jpg` }
  ];

  const results = {};

  for (const thumbnail of thumbnailTypes) {
    results[thumbnail.name] = await checkThumbnailExists(thumbnail.filename);
  }

  return results;
};

export const uploadThumbnail = async (blob: Blob, filename: string) => {
  const formData = new FormData();
  formData.append('file', blob, filename);
  formData.append('type', 'filesUpload');

  const response = await callClientPortal('/files/upload', {
    method: 'POST',
    body: formData
  });

  console.log(`✅ Upload successful for ${filename}:`, response.data);
  return response.data;
};

// Generate thumbnail using backend proxy
export const generateThumbnail = async (
  courseId: number,
  thumbnailType: string,
  delaySeconds: number = 8
) => {
  const response = await callClientPortal(`/thumbnail/${courseId}`, {
    method: 'POST',
    body: JSON.stringify({ thumbnailType, delaySeconds })
  });

  // Return the actual data, not the wrapped response
  return response.data;
};

// Export the helper function for use in components
export { getThumbnailBucketUrl };

// @ts-ignore
window.checkThumbnailExists = checkThumbnailExists;
// @ts-ignore
window.checkAllThumbnailsExist = checkAllThumbnailsExist;
// @ts-ignore
window.uploadThumbnail = uploadThumbnail;
// @ts-ignore
window.getThumbnailBucketUrl = getThumbnailBucketUrl;

// @ts-ignore
window.createSheet = async (
  version: string,
  compact: boolean = true,
  limit: number = 5000,
  download: boolean = true
) => {
  if (!version) return 'No version provided';
  if (!compact) limit = 1000; // cap the limit to 1000 for non-compact data

  const url =
    `/saved?&limit=${limit}&distinct=true&sort=${LATEST}` +
    (compact ? '&compact=true' : '');
  const response = await callClientPortal<CourseResponse>(url);

  const courses = response.data.data.results || [];

  // return courses;

  const tsv =
    'Course\t \t \t \tURLs\t \t \tContent\t \t \t \t \t \t \n' +
    'Name\tTitle\tType\tSaved\tAuthor\tLatest\tNext\tLines of CSS\tcourse:\tpages:\tpatches:\thidden:\tlanguages:\taudio:\n' +
    courses
      .map(({ firmCode, courseId, type, yaml, css, title, metadata }) => {
        const name = firmCode ? `${firmCode}_${courseId}` : `${courseId}`;

        const author = `https://authors.vinciworks.com/#/editor/${name}`;
        const latest = `https://portal.vinciworks.com/awscontent/ubercourse/latest/app.html?courseid=${courseId}&firm=${firmCode}`;
        const next = `https://portal.vinciworks.com/awscontent/ubercourse/${version}/app.html?courseid=${courseId}&firm=${firmCode}`;
        const updated = new Date(+metadata.createdDate).toLocaleDateString();

        const linesCss = css?.split('\n').length || 0;

        const y = {
          course: +/^course:/m.test(yaml),
          pages: +/^pages:/m.test(yaml),
          patches: +/^patches:/m.test(yaml),
          hidden: +/^hidden:/m.test(yaml),
          languages: +/^languages:/m.test(yaml),
          audio: +/^  audio:/m.test(yaml)
        };

        return [
          name,
          title?.trim().replace(/\s+/g, ' '),
          type,
          updated,
          author,
          latest,
          next,
          linesCss,
          ...Object.values(y)
        ].join('\t');
      })
      .join('\n');

  if (!download) {
    return tsv;
  }

  const ts = timestamp('YYYY-MM-DD_HH-mm-ss');
  const filename = `course-data_${ts}.tsv`;
  const temp = window.URL.createObjectURL(new Blob([tsv]));
  const link = document.createElement('a');
  link.href = temp;
  link.setAttribute('download', filename);
  link.classList.add('sr-only');
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(temp); // Clean up the URL object after download
  return 'Done!';
};

// ============================================================================
// JIT Lexicon Management
// ============================================================================

export interface LexiconFileStatus {
  filename: string;
  type: 'azure' | 'google';
  locale: string;
  revision: string | null;
  lastModified: string | null;
  etag: string | null;
  exists: boolean;
}

export interface LexiconStatus {
  env: string;
  bucket: string;
  /** Azure locale → revision (manifest `azure` section) */
  revisions: Record<string, string>;
  /** Google locale → revision (manifest `google` section) */
  googleRevisions: Record<string, string>;
  files: LexiconFileStatus[];
}

export interface LexiconRevisionsManifest {
  azure: Record<string, string>;
  google: Record<string, string>;
}

export interface LexiconPublishResult {
  uploaded: string[];
  errors: string[];
  manifest: LexiconRevisionsManifest;
  cacheInvalidated: boolean;
}

/** mod-content wraps payloads in `data.results` (see vinci-core-lib responseHandler). */
function unwrapModContentResults<T>(response: {
  data: { data?: { results?: T } & T };
}): T {
  const payload = response.data?.data;
  if (payload && typeof payload === 'object' && 'results' in payload) {
    return payload.results as T;
  }
  return payload as T;
}

export const getJitLexiconStatus = async (): Promise<LexiconStatus> => {
  const response = await callClientPortal('/audio/ai/jit/lexicon/status', {
    method: 'GET'
  });
  return unwrapModContentResults<LexiconStatus>(response);
};

export const getJitLexiconLiveContent = async (
  filename: string
): Promise<string> => {
  const response = await callClientPortal(
    `/audio/ai/jit/lexicon/content?filename=${encodeURIComponent(filename)}`,
    { method: 'GET' }
  );
  const data = unwrapModContentResults<{
    content: string | null;
    exists: boolean;
  }>(response);
  return data?.content ?? '';
};

export const publishJitLexicons = async (
  files: File[]
): Promise<LexiconPublishResult> => {
  const formData = new FormData();
  for (const file of files) {
    formData.append('files', file, file.name);
  }
  const response = await callClientPortal('/audio/ai/jit/lexicon/publish', {
    method: 'POST',
    body: formData
  });
  return unwrapModContentResults<LexiconPublishResult>(response);
};
